/* Shared date-formatting helpers.
 *
 * All formatters render abbreviated or unambiguous forms (e.g. "22 May 2026")
 * so the same string reads correctly in Turkish and English — no DD/MM vs MM/DD
 * confusion between locales. Month names follow the active language.
 */

const localeFor = (language) => (language === 'tr' ? 'tr-TR' : 'en-US');

const toDate = (value) => {
  if (value == null || value === '') return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  // Accept ISO date / datetime strings. For bare YYYY-MM-DD, parse as local.
  if (typeof value === 'string') {
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [y, m, d] = value.split('-').map(Number);
      return new Date(y, m - 1, d);
    }
    if (/^\d{4}-\d{2}$/.test(value)) {
      const [y, m] = value.split('-').map(Number);
      return new Date(y, m - 1, 1);
    }
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
};

/* "22 May 2026" / "22 Mar 2026" — day + abbreviated month + year. */
export const formatShortDate = (value, language = 'tr') => {
  const d = toDate(value);
  if (!d) return '—';
  return new Intl.DateTimeFormat(localeFor(language), {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(d);
};

/* "Mayıs 2026" / "May 2026" — full month + year, used as section headings. */
export const formatLongMonth = (value, language = 'tr') => {
  const d = toDate(value);
  if (!d) return '—';
  return new Intl.DateTimeFormat(localeFor(language), {
    month: 'long',
    year: 'numeric',
  }).format(d);
};

/* Returns YYYY-MM derived from any date-ish input. Stable across locales —
 * use this as a grouping key for "by month" sections. */
export const monthKey = (value) => {
  const d = toDate(value);
  if (!d) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

/* Sortable timestamp from a date-ish input (or 0 if unparseable). */
export const dateSortValue = (value) => {
  const d = toDate(value);
  return d ? d.getTime() : 0;
};
